import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ThemeContext, {
  ResolvedTheme,
  THEME_STORE_KEY,
  ThemePreference,
} from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

/* Browser UI color (mobile address bar, PWA title bar); matches --color-bg */
const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#f6f8f4',
  dark: '#0c111d',
};

const isExplicitTheme = (value: unknown): value is ResolvedTheme =>
  value === 'light' || value === 'dark';

const getStoredPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORE_KEY);
    return isExplicitTheme(stored) ? stored : 'system';
  } catch {
    // Storage can be blocked (private mode, site data disabled): follow the OS
    return 'system';
  }
};

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';

const updateThemeColorMeta = (preference: ThemePreference) => {
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      const metaTheme = meta.media.includes('dark') ? 'dark' : 'light';
      meta.content =
        THEME_COLORS[preference === 'system' ? metaTheme : preference];
    });
};

/*
 * "system" leaves <html> without data-theme so the CSS media query decides;
 * "light"/"dark" set data-theme, which overrides the OS (see theme.scss).
 */
const applyPreference = (preference: ThemePreference) => {
  const root = document.documentElement;
  if (preference === 'system') {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = preference;
  }
  updateThemeColorMeta(preference);
};

/*
 * Many components use `transition: all`, so without this a theme change would
 * morph each element at its own speed. Returns a cleanup for effects.
 */
const withoutTransitions = (change: () => void) => {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  change();
  window.getComputedStyle(root).getPropertyValue('color');
  const frame = requestAnimationFrame(() =>
    root.classList.remove('theme-switching')
  );

  return () => {
    cancelAnimationFrame(frame);
    root.classList.remove('theme-switching');
  };
};

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(getStoredPreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);
  const isFirstApply = useRef(true);

  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    // MediaQueryList fires before the browser restyles, so the class lands in time
    const onChange = () =>
      withoutTransitions(() =>
        setSystemTheme(media.matches ? 'dark' : 'light')
      );

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Keep other open tabs in step with a change made here
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORE_KEY) {
        setPreferenceState(
          isExplicitTheme(event.newValue) ? event.newValue : 'system'
        );
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    // The pre-paint script in index.html already applied the stored theme
    if (isFirstApply.current) {
      isFirstApply.current = false;
      applyPreference(preference);
      return;
    }

    return withoutTransitions(() => applyPreference(preference));
  }, [preference]);

  const setPreference = useCallback((value: ThemePreference) => {
    setPreferenceState(value);
    try {
      if (value === 'system') {
        localStorage.removeItem(THEME_STORE_KEY);
      } else {
        localStorage.setItem(THEME_STORE_KEY, value);
      }
    } catch {
      // Not persisted; the choice still applies for this visit
    }
  }, []);

  const resolvedTheme = preference === 'system' ? systemTheme : preference;

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};
